/**
 * Signing in through the old MyPoz website (MYP-125).
 *
 * The vendor's plan (Q&A v2, sign-in section): the customer signs in on the old website, a button
 * there opens this app with the login token in the address, and the app sends that token with every
 * call. There is no login endpoint yet; the token is valid for ten days.
 */

/** The address parameter the old website puts the token in. */
export const SESSION_TOKEN_PARAM = 'token';

export interface TokenFromUrl {
  /** The token, when the address carried a non-empty one. */
  readonly token?: string;
  /**
   * The same address without the token (path, other parameters and hash kept), for
   * `history.replaceState` — or undefined when there was no token parameter to remove.
   */
  readonly cleanUrl?: string;
}

/**
 * Reads the token out of an address and gives back the address without it.
 *
 * Every other parameter survives (`?type=proxy` on China Payment, say): only `token` is removed.
 * An empty `?token=` is removed too, but carries no token. The clean address is relative (path,
 * query, hash), which is what `history.replaceState` wants for the same document.
 */
export function takeTokenFromUrl(href: string): TokenFromUrl {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return {};
  }
  if (!url.searchParams.has(SESSION_TOKEN_PARAM)) return {};

  const token = url.searchParams.get(SESSION_TOKEN_PARAM)?.trim() ?? '';
  url.searchParams.delete(SESSION_TOKEN_PARAM);
  const cleanUrl = `${url.pathname}${url.search}${url.hash}`;

  return token ? { token, cleanUrl } : { cleanUrl };
}

/**
 * Whether a vendor reply means "your token is missing, wrong or expired" (MYP-125).
 *
 * The code alone is not enough: several endpoints use `-1` for other refusals too — `orders_create`
 * for packages from different warehouses, `uploadImg` for a bad file. What marks the token case is
 * the vendor's own wording, checked live (6 Oct 2026, any endpoint without a token):
 * `{"code":"-1","msg_cn":"Token无效","msg_en":"Invalid token"}`.
 *
 * Read from the raw reply body, before the endpoint's own code table replaces the message.
 */
export function isInvalidTokenReply(body: unknown): boolean {
  if (typeof body !== 'object' || body === null) return false;
  const record = body as Record<string, unknown>;
  if (String(record['code'] ?? '').trim() !== '-1') return false;
  const en = typeof record['msg_en'] === 'string' ? record['msg_en'] : '';
  const cn = typeof record['msg_cn'] === 'string' ? record['msg_cn'] : '';
  return /invalid token/i.test(en) || /token\s*无效/i.test(cn);
}

/**
 * The customer a token belongs to, read from its middle part — a JWT's payload, e.g.
 * `{"userid":"16562","userno":"MP66184",…}` — or null when it can't be read.
 *
 * Used only to tell "the same customer got a fresh token" from "a different customer signed in" when
 * another tab saves a token; nothing here is trusted for access, the vendor still checks every call.
 */
export function tokenCustomer(token: string): string | null {
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const data: unknown = JSON.parse(atob(padded));
    if (typeof data !== 'object' || data === null) return null;
    const record = data as Record<string, unknown>;
    const id = record['userno'] ?? record['userid'];
    return typeof id === 'string' || typeof id === 'number' ? String(id) : null;
  } catch {
    return null;
  }
}
